import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useState } from "react";
import type { FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  Contact,
  Heart,
  Home,
  IdCard,
  Image,
} from "lucide-react";

import { createHousehold, getHouseholds } from "@/api/households";
import type { MemberPayload, MemberResponse } from "@/api/members";
import { useAuth } from "@/features/auth/hooks/useAuth";

import { HouseholdEditor } from "@/features/households/components/HouseholdEditor";

type MemberFormProps = {
  onHouseholdPendingChange?: (pending: boolean) => void;
  member?: MemberResponse;
  isSubmitting?: boolean;
  submitLabel: string;
  onSubmit: (payload: MemberPayload) => void;
};

function emptyToNull(value: string) {
  return value.trim() === "" ? null : value.trim();
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-sm font-medium text-slate-300">{children}</span>;
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-950/80 p-5">
      <div className="mb-4 flex items-center gap-2">
        <div className="rounded-lg bg-blue-500/10 p-2 text-blue-400">
          {icon}
        </div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
          {title}
        </h3>
      </div>

      {children}
    </section>
  );
}

const inputClass =
  "w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

const selectClass =
  "w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

export function MemberForm({
  member,
  isSubmitting = false,
  submitLabel,
  onSubmit,
  onHouseholdPendingChange,
}: MemberFormProps) {
  const { churchId } = useAuth();
  const queryClient = useQueryClient();
  const [householdPending, setHouseholdPending] = useState(false);
  const [householdCreated, setHouseholdCreated] = useState(false);
  const { permissions } = useEffectivePermissions();
  const canHouseholds = permissions.has("HOUSEHOLDS_VIEW") && permissions.has("HOUSEHOLDS_MANAGE");

  const { data: households = [] } = useQuery({
    queryKey: ["households", churchId],
    enabled: !!churchId && canHouseholds,
    queryFn: () => getHouseholds(churchId!),
  });

  const [classification, setClassification] = useState<"" | "REGULAR" | "YOUTH">("");
  const [youthStart, setYouthStart] = useState("");
  const [firstName, setFirstName] = useState(member?.first_name ?? "");
  const [middleName, setMiddleName] = useState(member?.middle_name ?? "");
  const [lastName, setLastName] = useState(member?.last_name ?? "");
  const [preferredName, setPreferredName] = useState(
    member?.preferred_name ?? ""
  );
  const [photoUrl, setPhotoUrl] = useState(member?.photo_url ?? "");

  const [email, setEmail] = useState(member?.email ?? "");
  const [phone, setPhone] = useState(member?.phone ?? "");

  const [status, setStatus] = useState(member?.status ?? "ACTIVE");
  const [memberType, setMemberType] = useState(member?.member_type ?? "MEMBER");
  const [dateJoined, setDateJoined] = useState(member?.date_joined ?? "");

  const [householdMode, setHouseholdMode] = useState<
    "none" | "existing" | "new"
  >(member?.household_id ? "existing" : "none");
  const [householdId, setHouseholdId] = useState(member?.household_id ?? "");
  const [relationship, setRelationship] = useState(
    member?.relationship_to_household ?? ""
  );
  const [headOfHousehold, setHeadOfHousehold] = useState(
    member?.is_head_of_household ?? false
  );

  const [birthDate, setBirthDate] = useState(member?.birth_date ?? "");
  const [hideBirthYear, setHideBirthYear] = useState(
    member?.hide_birth_year ?? true
  );

  const [anniversaryDate, setAnniversaryDate] = useState(
    member?.anniversary_date ?? ""
  );
  const [hideAnniversaryYear, setHideAnniversaryYear] = useState(
    member?.hide_anniversary_year ?? true
  );

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting || householdPending || householdMode === "new") return;

    onSubmit({
      ...(!member && classification ? { attendance_classification: classification, youth_joined_at: classification === "YOUTH" ? new Date(youthStart).toISOString() : null } : {}),
      first_name: firstName.trim(),
      middle_name: emptyToNull(middleName),
      last_name: lastName.trim(),
      preferred_name: emptyToNull(preferredName),
      photo_url: emptyToNull(photoUrl),

      email: emptyToNull(email),
      phone: emptyToNull(phone),

      status,
      member_type: memberType,
      date_joined: emptyToNull(dateJoined),

      birth_date: emptyToNull(birthDate),
      hide_birth_year: hideBirthYear,

      anniversary_date: emptyToNull(anniversaryDate),
      hide_anniversary_year: hideAnniversaryYear,

      ...(canHouseholds ? {
        household_id: householdMode === "existing" ? householdId : null,
        household_name: null,
        relationship_to_household: householdMode === "none" ? null : relationship || null,
        is_head_of_household: householdMode !== "none" && headOfHousehold,
      } : {}),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {!member && <Section icon={<IdCard className="h-4 w-4" />} title="Initial ministry enrollment">
        <label className="block space-y-2"><FieldLabel>Ministry enrollment *</FieldLabel>
          <select required className={selectClass} value={classification} onChange={e => setClassification(e.target.value as typeof classification)}>
            <option value="">Choose enrollment</option><option value="REGULAR">No Youth Ministry enrollment (Main Church services)</option><option value="YOUTH">Enroll in Youth Ministry</option>
          </select>
        </label>
        {classification === "YOUTH" && <label className="mt-3 block space-y-2"><FieldLabel>Ministry enrollment starts (local time) *</FieldLabel>
          <input required type="datetime-local" className={inputClass} value={youthStart} onChange={e => setYouthStart(e.target.value)} />
        </label>}
      </Section>}
      <Section icon={<IdCard className="h-4 w-4" />} title="Identity">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <FieldLabel>First Name *</FieldLabel>
            <input
              required
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              placeholder="First name"
              className={inputClass}
            />
          </label>

          <label className="space-y-2">
            <FieldLabel>Middle Name</FieldLabel>
            <input
              value={middleName}
              onChange={(event) => setMiddleName(event.target.value)}
              placeholder="Middle name"
              className={inputClass}
            />
          </label>

          <label className="space-y-2">
            <FieldLabel>Last Name *</FieldLabel>
            <input
              required
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              placeholder="Last name"
              className={inputClass}
            />
          </label>

          <label className="space-y-2">
            <FieldLabel>Preferred Name</FieldLabel>
            <input
              value={preferredName}
              onChange={(event) => setPreferredName(event.target.value)}
              placeholder="Preferred name"
              className={inputClass}
            />
          </label>
        </div>
      </Section>

      <Section icon={<Image className="h-4 w-4" />} title="Photo">
        <div className="flex gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-700 bg-slate-900">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt="Member"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-lg font-semibold text-slate-500">
                {firstName?.[0]}
                {lastName?.[0]}
              </span>
            )}
          </div>

          <label className="flex-1 space-y-2">
            <FieldLabel>Photo URL</FieldLabel>
            <input
              value={photoUrl}
              onChange={(event) => setPhotoUrl(event.target.value)}
              placeholder="https://..."
              className={inputClass}
            />
          </label>
        </div>
      </Section>

      <Section icon={<Contact className="h-4 w-4" />} title="Contact">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <FieldLabel>Email</FieldLabel>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@example.com"
              className={inputClass}
            />
          </label>

          <label className="space-y-2">
            <FieldLabel>Phone</FieldLabel>
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="555-123-4567"
              className={inputClass}
            />
          </label>
        </div>
      </Section>

      <Section icon={<Heart className="h-4 w-4" />} title="Membership">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="space-y-2">
            <FieldLabel>Status</FieldLabel>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className={selectClass}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="VISITOR">Visitor</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </label>

          <label className="space-y-2">
            <FieldLabel>Member Type</FieldLabel>
            <select
              value={memberType}
              onChange={(event) => setMemberType(event.target.value)}
              className={selectClass}
            >
              <option value="MEMBER">Member</option>
              <option value="VISITOR">Visitor</option>
              <option value="LEADER">Leader</option>
              <option value="STAFF">Staff</option>
            </select>
          </label>

          <label className="space-y-2">
            <FieldLabel>Date Joined</FieldLabel>
            <input
              type="date"
              value={dateJoined}
              onChange={(event) => setDateJoined(event.target.value)}
              className={inputClass}
            />
          </label>
        </div>
      </Section>

      {canHouseholds && <Section icon={<Home className="h-4 w-4" />} title="Household">
        <div className="space-y-4">
          <label className="space-y-2">
            <FieldLabel>Household</FieldLabel>

            <select
              disabled={householdPending || isSubmitting}
              value={householdMode}
              onChange={(event) =>
                setHouseholdMode(
                  event.target.value as "none" | "existing" | "new"
                )
              }
              className={selectClass}
            >
              <option value="none">No Household</option>
              <option value="existing">Existing Household</option>
              <option value="new">Create New Household</option>
            </select>
          </label>

          {member?.is_head_of_household && (householdMode === "none" || householdMode === "new" || householdId !== member.household_id) &&
            <p className="text-sm text-amber-300">This member is the head of household. Removing them will leave this household without a designated head.</p>}
          {householdMode === "none" && member?.household_id && <p className="text-sm text-slate-400">Saving removes only the household association. The member and their attendance, care, ministry and other history remain.</p>}
          {householdMode === "existing" && (
            <label className="space-y-2">
              <FieldLabel>Existing Household</FieldLabel>

              <select
                value={householdId}
                onChange={(event) => { if (event.target.value !== householdId) setHeadOfHousehold(false); setHouseholdId(event.target.value); }}
                className={selectClass}
                required
              >
                <option value="">Select household...</option>

                {households.map((household) => (
                  <option key={household.id} value={household.id}>
                    {household.household_name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {householdMode === "new" && (
            <HouseholdEditor inline onPendingChange={pending => {
              setHouseholdPending(pending);
              onHouseholdPendingChange?.(pending);
            }} onSave={async payload => {
              const household = await createHousehold(churchId!, payload);
              queryClient.setQueryData<Awaited<ReturnType<typeof getHouseholds>>>(["households", churchId], previous =>
                [...(previous ?? []).filter(item => item.id !== household.id), household]);
              setHouseholdId(household.id);
              setHeadOfHousehold(false);
              setHouseholdMode("existing");
              setHouseholdCreated(true);
              void queryClient.invalidateQueries({ queryKey: ["households", churchId] });
            }} />
          )}
          {householdCreated && <p role="status" className="text-sm text-emerald-300">Household created and selected. Save the member to assign them to this household.</p>}

          {householdMode !== "none" && (
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <FieldLabel>Relationship</FieldLabel>

                <select
                  required
                  value={relationship}
                  onChange={(event) => setRelationship(event.target.value)}
                  className={selectClass}
                >
                  <option value="">Select...</option>
                  <option value="SELF">Self</option>
                  <option value="SPOUSE">Spouse</option>
                  <option value="CHILD">Child</option>
                  <option value="PARENT">Parent</option>
                  <option value="OTHER">Other</option>
                </select>
              </label>

              <label className="flex items-end gap-3 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={headOfHousehold}
                  onChange={(event) =>
                    setHeadOfHousehold(event.target.checked)
                  }
                />
                Head of Household
              </label>
            </div>
          )}
        </div>
      </Section>}

      <Section
        icon={<CalendarDays className="h-4 w-4" />}
        title="Important Dates"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <FieldLabel>Birth Date</FieldLabel>
            <input
              type="date"
              value={birthDate}
              onChange={(event) => setBirthDate(event.target.value)}
              className={inputClass}
            />
          </label>

          <label className="flex items-end gap-3 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={hideBirthYear}
              onChange={(event) => setHideBirthYear(event.target.checked)}
              className="h-4 w-4"
            />
            Hide birth year
          </label>

          <label className="space-y-2">
            <FieldLabel>Anniversary Date</FieldLabel>
            <input
              type="date"
              value={anniversaryDate}
              onChange={(event) => setAnniversaryDate(event.target.value)}
              className={inputClass}
            />
          </label>

          <label className="flex items-end gap-3 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={hideAnniversaryYear}
              onChange={(event) =>
                setHideAnniversaryYear(event.target.checked)
              }
              className="h-4 w-4"
            />
            Hide anniversary year
          </label>
        </div>
      </Section>

      <div className="sticky bottom-0 -mx-6 -mb-6 flex justify-end border-t border-slate-800 bg-slate-950 px-6 py-4">
        <button
          type="submit"
          disabled={isSubmitting || householdPending || householdMode === "new"}
          className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </button>
      </div>
    </form>
  );
}